import {
  ArrowLeftOutlined,
  LockOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { Button, Card, Form, Input, Progress, Tabs, Typography } from "antd";
import CryptoJS from "crypto-js";
import dayjs from "dayjs";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { generateOtp, getLogin, saveUser } from "../../api/NoAuth";
import { useGlobalUI } from "../../context/GlobalUIProvider";
import AuthService from "../../session/AuthService";
import BasicInfoForm from "./BasicInfoForm";
import KYCInformations from "./KYCInformations";
import SecuritySetupForm from "./SecuritySetupForm";
import WalletActivated from "./WalletActivated";

const { Title, Paragraph, Text } = Typography;

const AuthForm = () => {
  const { message, showLoader, hideLoader } = useGlobalUI();

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({
    mobileNo: "",
    otp: "",
    dob: null,
    gender: "",
    nationality: "",
    refferalCode: "",
    pinNo: "",
    confirmPin: "",
    loginPassword: "",
    confirmPassword: "",
  });

  const [activeStep, setActiveStep] = useState("signin");
  const [otpSent, setOtpSent] = useState(false);
  const [step, setStep] = useState(0);

  const [checkAuth, setCheckAuth] = useState(AuthService.isAuth());

  useEffect(() => {
    setOtpSent(false);
    setStep(0);
    form.resetFields();
    setFormValues({
      mobileNo: "",
      otp: "",
      dob: null,
      gender: "",
      nationality: "",
      refferalCode: "",
      pinNo: "",
      confirmPin: "",
      loginPassword: "",
      confirmPassword: "",
    });
  }, [activeStep]);

  useEffect(() => {
    if (checkAuth) {
      navigate("/walletDashboard");
    }
  }, [checkAuth]);

  const handleGetOTP = () => {
    if (formValues.mobileNo.trim()) {
      if (activeStep === "signup") {
        setOtpSent(true);
      } else {
        showLoader();
        let obj = {
          mobileNo: formValues.mobileNo.trim(),
        };
        generateOtp(obj)
          .then((response) => {
            if (response.data.status === 1) {
              message.success("OTP sent successfully");
            } else {
              message.error(response.data.messgae);
            }
          })
          .catch((error) => {
            message.error(error);
          })
          .finally(() => hideLoader());
        setOtpSent(true);
      }
    }
  };

  const handleVerifyOTP = () => {
    if (step === 3) {
      handleSubmitForm();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSignIn = () => {
    let obj = {
      otp: formValues.otp,
      mobileNo: formValues.mobileNo,
    };
    // validateOtp(obj).then((response) => {
    // if (response.data.status === 1) {
    const loginUser = {
      mobileNo: formValues.mobileNo,
      password: CryptoJS.MD5("Pass12").toString(),
    };

    showLoader();

    getLogin(loginUser)
      .then((response) => {
        const result = response.data;

        if (result.status === 1) {
          const { token, ...userDetails } = result.data; // extract token & rest (including uType)
          AuthService.registerToken(token);
          AuthService.registerUserData(userDetails);

          message.success("Login successful!");

          // Now userDetails.uType is available
          if (userDetails.uType === 1) {
            navigate("/admin/dashboard");
          } else if (userDetails.uType === 2) {
            navigate("/admin/bulkTransactions");
          } else if (userDetails.uType === 3) {
            navigate("/admin/receivePayment");
          } else if (userDetails.uType === 4 || userDetails.uType === 5) {
            navigate("/admin/user-management");
          } else if (userDetails.uType === 6) {
            navigate("/walletDashboard");
          } else {
            navigate("/login");
          }
        } else if (result.status === 2) {
          message.error("The user does not have access to log in.");
        } else if (result.status === -1 || result.status === -2) {
          message.error(result.message || "Account issue, contact admin.");
        } else {
          message.error("Please try with valid credentials.");
        }
      })
      .catch((error) => {
        console.error(error);
        message.error("Network error, Please try again later.");
      })
      .finally(() => {
        hideLoader();
      });
    // } else {
    //   message.error(response.data.message);
    // }
    // });
  };

  const handleStep = (e) => {
    setActiveStep(e);
  };
  const handleSubmitForm = async () => {
    try {
      showLoader();

      // Prepare FormData
      const formData = new FormData();

      // Append all non-file fields
      formData.append(
        "cloginPassword",
        CryptoJS.MD5(formValues.confirmPassword).toString()
      );
      formData.append("userType", 6);

      Object.entries(formValues).forEach(([key, value]) => {
        if (key !== "documentFile") {
          if (dayjs.isDayjs(value)) {
            // Convert dayjs date to string
            formData.append(key, value.format("YYYY-MM-DD"));
          } else if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        }
      });

      // Append file field (if uploaded)

      if (formValues.addressProofFile?.length > 0) {
        formData.append(
          "addressProofFile",
          formValues.addressProofFile[0].originFileObj
        );
      }

      if (formValues.idProofFile?.length > 0) {
        formData.append("idProofFile", formValues.idProofFile[0].originFileObj);
      }

      if (formValues.otherProofFile?.length > 0) {
        formData.append(
          "otherProofFile",
          formValues.otherProofFile[0].originFileObj
        );
      }

      // Send FormData to API
      const response = await saveUser(formData); // ✅ saveUser should accept FormData
      if (response?.data?.status === 1) {
        const userData = response?.data?.data?.userData;

        setFormValues({
          ...userData,
          dob: userData.dob ? dayjs(userData.dob) : null,
        });

        AuthService.registerToken(response?.data?.data?.token);
        message.success("User saved successfully");
        setStep(step + 1);
      } else {
        message.error(response?.data?.message || "Failed to save user");
      }
    } catch (error) {
      console.error("Save failed", error);
      message.error("Validation failed or API error.");
    } finally {
      hideLoader();
    }
  };

  const onFormChange = (changedValues, allValues) => {
    console.log("Changed:", changedValues);
    console.log("All:", allValues);
  };

  console.log("FormValues", formValues);

  const onChangeFormValues = (name, value) => {
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value };
      form.setFieldsValue({ [name]: value });
      return updated;
    });
  };

  const handleChangeNumber = () => {
    setOtpSent(false);
    form.resetFields(["otp"]);
    setFormValues({ ...formValues, otp: "" });
  };

  return (
    <div
      className="min-vh-100 bg-light d-flex flex-column align-items-center"
      style={{ minHeight: "100vh" }}
    >
      {/* Full-width Header with Blue Background */}
      <div
        style={{
          width: "100%",
          background:
            step === 0
              ? "linear-gradient(to right, #021B79,#0575E6)"
              : "#6c2bd9",
        }}
      >
        <div
          className="mx-auto d-flex align-items-center"
          style={{
            maxWidth: 400,
            padding: "1.5rem 1rem",
            position: "relative",
          }}
        >
          {/* Back Icon */}
          {step > 0 && (
            <ArrowLeftOutlined
              onClick={handleBack}
              style={{
                fontSize: 18,
                color: "white",
                position: "absolute",
                left: 16,
                top: 40,
                cursor: "pointer",
              }}
            />
          )}

          <div className="text-center w-100">
            <Title
              level={2}
              style={{
                color: "white",
                marginBottom: 0,
                fontSize: "clamp(1.5rem, 5vw, 2rem)",
              }}
            >
              📱 Wallet Pro
            </Title>

            {step === 0 ? (
              <Paragraph style={{ color: "white", marginBottom: 0 }}>
                Your Digital Wallet Solution
              </Paragraph>
            ) : step === 1 ? (
              <>
                <Text style={{ color: "white" }}>Step 1 of 4</Text>
                <div style={{ padding: "8px 24px" }}>
                  <Progress percent={30} showInfo={false} strokeColor="white" />
                </div>
                <Paragraph style={{ color: "white", marginBottom: 0 }}>
                  Basic Information
                </Paragraph>
              </>
            ) : step === 2 ? (
              <>
                <Text style={{ color: "white" }}>Step 2 of 4</Text>
                <div style={{ padding: "8px 24px" }}>
                  <Progress percent={50} showInfo={false} strokeColor="white" />
                </div>
                <Paragraph style={{ color: "white", marginBottom: 0 }}>
                  KYC Information
                </Paragraph>
              </>
            ) : step === 3 ? (
              <>
                <Text style={{ color: "white" }}>Step 3 of 4</Text>
                <div style={{ padding: "8px 24px" }}>
                  <Progress percent={70} showInfo={false} strokeColor="white" />
                </div>
                <Paragraph style={{ color: "white", marginBottom: 0 }}>
                  Security Setup
                </Paragraph>
              </>
            ) : step === 4 ? (
              <>
                <Text style={{ color: "white" }}>Step 4 of 4</Text>
                <div style={{ padding: "8px 24px" }}>
                  <Progress
                    percent={100}
                    showInfo={false}
                    strokeColor="white"
                  />
                </div>
                <Paragraph style={{ color: "white", marginBottom: 0 }}>
                  Wallet Activation
                </Paragraph>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Card Section below header */}
      {step === 0 ? (
        <Card
          style={{ borderRadius: 12 }}
          bodyStyle={{ padding: "24px 24px 8px" }}
        >
          <Title level={4} style={{ textAlign: "center" }}>
            Get Started
          </Title>
          <Tabs
            defaultActiveKey="signup"
            activeKey={activeStep}
            centered
            onChange={handleStep}
          >
            {["signup", "signin"].map((tabKey) => {
              const isSignIn = tabKey === "signin";
              return (
                <Tabs.TabPane
                  tab={isSignIn ? "Sign In" : "Sign Up"}
                  key={tabKey}
                >
                  <Paragraph strong className="text-center">
                    {isSignIn ? "Welcome Back" : "Create New Account"}
                  </Paragraph>
                  <Paragraph type="secondary" className="text-center mb-4">
                    {isSignIn
                      ? "Enter your mobile number to sign in"
                      : "Enter your mobile number to get started"}
                  </Paragraph>

                  <Form
                    layout="vertical"
                    form={form}
                    onValuesChange={onFormChange}
                  >
                    {otpSent && activeStep === "signup" && (
                      <Paragraph
                        style={{
                          color: "#1d4ed8",
                          textAlign: "center",
                          marginBottom: 12,
                          fontWeight: 500,
                          fontSize: 12,
                        }}
                      >
                        Demo OTP: <strong>123456</strong>
                      </Paragraph>
                    )}

                    {!otpSent ? (
                      <Form.Item
                        label="Mobile Number"
                        name="mobileNo"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your mobile number",
                          },
                          {
                            pattern: /^[0-9]{10,15}$/,
                            message: "Enter a valid mobile number",
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          size="large"
                          prefix={<PhoneOutlined />}
                          placeholder="+234 800 000 0000"
                          name="mobileNo"
                          value={formValues.mobileNo}
                          onChange={(e) =>
                            onChangeFormValues("mobileNo", e.target.value)
                          }
                        />
                      </Form.Item>
                    ) : (
                      <>
                        <Form.Item
                          label="OTP"
                          name="otp"
                          rules={[
                            { required: true, message: "Please enter the OTP" },
                            { len: 6, message: "OTP must be 6 digits" },
                          ]}
                        >
                          <Input
                            type="number"
                            size="large"
                            prefix={<LockOutlined />}
                            placeholder="Enter 6-digit OTP"
                            maxLength={6}
                            name="otp"
                            value={formValues.otp}
                            onChange={(e) =>
                              onChangeFormValues("otp", e.target.value)
                            }
                          />
                        </Form.Item>

                        <Form.Item>
                          <Button
                            type="primary"
                            block
                            size="large"
                            style={{
                              backgroundColor: "white",
                              borderColor: "black",
                              color: "black",
                            }}
                            onClick={handleChangeNumber}
                          >
                            <ArrowLeft size={15} /> Change Number
                          </Button>
                        </Form.Item>
                      </>
                    )}

                    <Form.Item>
                      <Button
                        type="primary"
                        size="large"
                        block
                        style={{
                          backgroundColor: isSignIn ? "#00b96b" : "#0575E6",
                          borderColor: isSignIn ? "#00b96b" : "#0575E6",
                        }}
                        onClick={() => {
                          form
                            .validateFields([!otpSent ? "mobileNo" : "otp"])
                            .then(() => {
                              if (!otpSent) {
                                handleGetOTP();
                              } else {
                                isSignIn ? handleSignIn() : handleVerifyOTP();
                              }
                            })
                            .catch(() => {});
                        }}
                      >
                        {otpSent
                          ? isSignIn
                            ? "Sign In"
                            : "Verify OTP"
                          : "Get OTP"}
                      </Button>
                    </Form.Item>
                  </Form>

                  <Paragraph
                    type="secondary"
                    className="text-center mt-3"
                    style={{ fontSize: 12 }}
                  >
                    By continuing, you agree to our Terms & Privacy Policy
                  </Paragraph>
                </Tabs.TabPane>
              );
            })}
          </Tabs>
        </Card>
      ) : step === 1 ? (
        <BasicInfoForm
          onContinue={handleVerifyOTP}
          form={form}
          onFormChange={onFormChange}
          formValues={formValues}
          onChangeFormValues={onChangeFormValues}
        />
      ) : step === 2 ? (
        <KYCInformations
          onContinue={handleVerifyOTP}
          form={form}
          onFormChange={onFormChange}
          formValues={formValues}
          onChangeFormValues={onChangeFormValues}
        />
      ) : step === 3 ? (
        <SecuritySetupForm
          onContinue={handleVerifyOTP}
          form={form}
          onFormChange={onFormChange}
          formValues={formValues}
          onChangeFormValues={onChangeFormValues}
        />
      ) : step === 4 ? (
        <WalletActivated
          onContinue={handleVerifyOTP}
          form={form}
          onFormChange={onFormChange}
          formValues={formValues}
          onChangeFormValues={onChangeFormValues}
        />
      ) : (
        ""
      )}
    </div>
  );
};

export default AuthForm;
